// Ad-click attribution capture for offline conversion tracking.
//
// Ad traffic arrives at tork.digitaldomination.ai/trial carrying tracking
// params (gclid/gbraid/wbraid/fbclid + utm_*). Those params are lost by the
// time a Stripe Checkout Session is created, so we stash them in localStorage
// under a single key the moment any page loads, then forward them into the
// Stripe session metadata when the $1 trial checkout is created. The webhook
// reads them back to attribute paid trials to the exact ad click.

export const ATTRIBUTION_KEY = "tork_attribution";

// Click identifiers: a fresh non-empty value on a new visit should update the
// stored value (the click that actually converts is the one that matters).
const CLICK_ID_FIELDS = ["gclid", "gbraid", "wbraid", "fbclid"] as const;
// Campaign params: first-touch wins (keep the campaign that originally landed
// the visitor); never overwrite an existing value.
const UTM_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

// All keys we persist (used by the server allow-list too).
export const ATTRIBUTION_FIELDS = [
  ...CLICK_ID_FIELDS,
  ...UTM_FIELDS,
  "landing_page",
  "referrer",
  "captured_at",
] as const;

export type Attribution = Partial<Record<(typeof ATTRIBUTION_FIELDS)[number], string>>;

function readStored(): Attribution {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Attribution) : {};
  } catch {
    return {};
  }
}

// Read tracking params from the current URL and merge them into the stored
// attribution object. Safe to call on every page load — it only writes when
// something actually changes, and never wipes a stored value with an empty one.
export function captureAttribution(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const stored = readStored();
    let changed = false;

    const write = (key: string, value: string | null) => {
      const v = (value ?? "").trim();
      if (!v) return; // never overwrite with empty
      const next = v.slice(0, 500); // Stripe metadata value cap
      if (stored[key as keyof Attribution] !== next) {
        stored[key as keyof Attribution] = next;
        changed = true;
      }
    };

    // Click IDs: last-touch (a fresh value updates).
    for (const k of CLICK_ID_FIELDS) write(k, params.get(k));
    // UTM: first-touch (only fill if not already stored).
    for (const k of UTM_FIELDS) {
      if (!stored[k]) write(k, params.get(k));
    }

    // Context, captured once on first touch.
    if (!stored.landing_page) {
      stored.landing_page = (window.location.pathname + window.location.search).slice(0, 500);
      changed = true;
    }
    if (!stored.referrer && document.referrer) {
      stored.referrer = document.referrer.slice(0, 500);
      changed = true;
    }
    if (!stored.captured_at) {
      stored.captured_at = new Date().toISOString();
      changed = true;
    }

    if (changed) localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(stored));
  } catch {
    // localStorage unavailable (private mode etc.) — silently no-op.
  }
}

// Returns the stored attribution object (empty for organic/direct visitors).
export function getStoredAttribution(): Attribution {
  return readStored();
}
